import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card } from '@tremor/react';
import { supabase } from '../lib/supabase';
import { usePopup } from '../contexts/PopupContext';
import MapView from '../components/discover/MapView';
import UpcomingEvents from '../components/discover/UpcomingEvents';
import PopularCategories from '../components/discover/PopularCategories';
import RecentActivity from '../components/discover/RecentActivity';
import DiscoverWidgetsDrawer from '../components/discover/DiscoverWidgetsDrawer';
import { formatLocalYmd, isEventNotFinishedYet } from '../utils/eventSchedule';
import './DiscoverPage.css';

// Definicja kategorii z typami eventów
const CATEGORY_EVENT_TYPES = {
  all: null, // null = wszystkie eventy
  sport: ['sport'],
  party: ['party'],
  gaming: ['gaming'],
  hangout: ['hangout'],
  trip: ['trip'],
  other: ['other'], // + nieznane typy
};

// Znane typy eventów (do filtrowania "other")
const KNOWN_EVENT_TYPES = new Set(['sport', 'party', 'gaming', 'hangout', 'trip', 'other']);

/** Zgodne z `DiscoverWidgetsDrawer.css` (@media max-width: 768px) — na mobile szuflada startuje zwinięta. */
const WIDGETS_DRAWER_MOBILE_MAX_PX = 768;

function getInitialWidgetsDrawerOpen() {
  if (typeof window === 'undefined') return true;
  return !window.matchMedia(`(max-width: ${WIDGETS_DRAWER_MOBILE_MAX_PX}px)`).matches;
}

function DiscoverPage() {
  const { openPopup } = usePopup();
  /** Cel Fullscreen API przy mapie — cały rząd mapa + szuflada (panel widoczny w pełnym ekranie). */
  const discoverMapRowFullscreenRef = useRef(null);
  const [allEvents, setAllEvents] = useState([]);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [widgetsDrawerOpen, setWidgetsDrawerOpen] = useState(getInitialWidgetsDrawerOpen);

  const getTodayDateString = useCallback(() => formatLocalYmd(new Date()), []);

  const sortEventsByDate = useCallback((eventsList) => {
    return [...eventsList].sort((a, b) => {
      const dateA = `${a.event_date || ''}T${a.start_time || '00:00:00'}`;
      const dateB = `${b.event_date || ''}T${b.start_time || '00:00:00'}`;
      return dateA.localeCompare(dateB);
    });
  }, []);

  const closeWidgetsDrawer = useCallback(() => setWidgetsDrawerOpen(false), []);

  const toggleWidgetsDrawer = useCallback(() => {
    setWidgetsDrawerOpen((v) => !v);
  }, []);

  useEffect(() => {
    if (!widgetsDrawerOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeWidgetsDrawer();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [widgetsDrawerOpen, closeWidgetsDrawer]);

  // Pobierz wszystkie eventy
  useEffect(() => {
    const loadAllEvents = async () => {
      try {
        setDashboardLoading(true);
        const todayString = getTodayDateString();

        let query = supabase
          .from('events')
          .select('*')
          .gte('event_date', todayString)
          .order('event_date', { ascending: true })
          .order('start_time', { ascending: true })
          .limit(200);

        try {
          query = query.eq('is_cancelled', false);
        } catch (err) {
          // Column may not exist yet.
        }

        let { data, error } = await query;

        if (error && error.message && error.message.includes('is_cancelled')) {
          query = supabase
            .from('events')
            .select('*')
            .gte('event_date', todayString)
            .order('event_date', { ascending: true })
            .order('start_time', { ascending: true })
            .limit(200);
          const retryResult = await query;
          data = retryResult.data;
          error = retryResult.error;
        }

        if (error) {
          console.error('Error loading events:', error);
          setAllEvents([]);
          return;
        }

        const filteredData = (data || []).filter((event) => isEventNotFinishedYet(event));
        setAllEvents(sortEventsByDate(filteredData));
      } catch (err) {
        console.error('Error loading events:', err);
        setAllEvents([]);
      } finally {
        setDashboardLoading(false);
      }
    };

    loadAllEvents();
  }, [getTodayDateString, sortEventsByDate]);

  useEffect(() => {
    const channelName = `discover-events-${Math.random().toString(36).slice(2, 10)}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'events' },
        (payload) => {
          setAllEvents((prev) => {
            if (payload.eventType === 'DELETE') {
              const deletedId = payload.old?.id;
              if (!deletedId) return prev;
              return prev.filter((event) => event.id !== deletedId);
            }

            const row = payload.new;
            if (!row?.id) return prev;

            const withoutCurrent = prev.filter((event) => event.id !== row.id);
            if (!isEventNotFinishedYet(row)) {
              return withoutCurrent;
            }
            return sortEventsByDate([...withoutCurrent, row]);
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [sortEventsByDate]);

  // Filtruj eventy według aktywnej kategorii
  const filteredEvents = useMemo(() => {
    if (!allEvents.length) return [];

    const eventTypes = CATEGORY_EVENT_TYPES[activeCategory];

    // Kategoria "all" - wszystkie eventy
    if (!eventTypes) {
      return allEvents;
    }

    // Kategoria "other" - nieznane typy + explicite "other"
    if (activeCategory === 'other') {
      return allEvents.filter(event => {
        const type = event.event_type;
        return !KNOWN_EVENT_TYPES.has(type) || type === 'other';
      });
    }

    // Pozostałe kategorie - filtruj po typie
    return allEvents.filter(event => eventTypes.includes(event.event_type));
  }, [allEvents, activeCategory]);

  // Zmiana kategorii
  const handleCategoryChange = useCallback((category) => {
    setActiveCategory(category);
  }, []);

  const handleDismissMapCategoryChip = useCallback(() => {
    setActiveCategory('all');
  }, []);

  const mapColumn = (
    <div className="map-column">
      <Card className="map-widget-card">
        <div className="map-widget-body">
          <MapView
            events={filteredEvents}
            isLoading={dashboardLoading}
            drawerBackdropOpen={widgetsDrawerOpen}
            onDrawerBackdropClose={closeWidgetsDrawer}
            eventCategory={activeCategory}
            onDismissEventCategory={handleDismissMapCategoryChip}
            fullscreenHostRef={discoverMapRowFullscreenRef}
          />
          <div className="map-floating-controls">
            <button
              type="button"
              className="map-expand-btn"
              onClick={() => openPopup('discover-map', { category: activeCategory }, 0)}
              title="Otwórz pełny widok mapy"
            >
              ⛶
            </button>
          </div>
        </div>
      </Card>
    </div>
  );

  const widgetsStack = (
    <div className="widgets-container">
      <PopularCategories activeCategory={activeCategory} onCategoryChange={handleCategoryChange} />
      <UpcomingEvents events={filteredEvents} isLoading={dashboardLoading} />
      <RecentActivity />
    </div>
  );

  return (
    <div className="discover-page discover-page--map-edge">
      <div className="page-container">
        <main className="discover-content discover-content--browse-tabs">
          <div ref={discoverMapRowFullscreenRef} className="discover-layout discover-layout--map-drawer">
            <div className="discover-map-shell">
              {mapColumn}
            </div>
            <DiscoverWidgetsDrawer isOpen={widgetsDrawerOpen} onToggle={toggleWidgetsDrawer}>
              {widgetsStack}
            </DiscoverWidgetsDrawer>
          </div>
        </main>
      </div>
    </div>
  );
}

export default DiscoverPage;
